import React, { useEffect, useState } from 'react'
import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCol,
  CContainer,
  CForm,
  CFormInput,
  CInputGroup,
  CRow,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilPencil, cilTrash } from '@coreui/icons'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import swal from 'sweetalert'
import { addCoupon, deleteCoupon, getCoupons, updateCoupon } from 'src/redux/coupons'
import { DateTimeConverter, DateYYYYMMDD, isExpired } from 'src/utils/function'

const Coupons = () => {
  const [formData, setFormData] = useState({
    discount: '',
    expiration_date: '',
    code: '',
  })

  const dispatch = useDispatch()

  const { coupons } = useSelector((state) => state.coupons)

  useEffect(() => {
    dispatch(getCoupons())
  }, [dispatch])

  const handleSubmit = async () => {
    if (!formData.discount) {
      toast('Discount required!', { type: 'info' })
    } else if (!formData.expiration_date) {
      toast('Expiration date required!', { type: 'info' })
    } else {
      const body = {
        discount: formData.discount,
        expiration_date: formData.expiration_date,
        code: formData.code,
      }

      try {
        await dispatch(addCoupon(body))
        setFormData({
          discount: '',
          expiration_date: '',
          code: '',
        })
      } catch (error) {
        toast(error.message, { type: 'error' })
      }
    }
  }

  async function handleDelete(id) {
    await swal({
      title: 'Are you sure? ',
      text: 'You want to delete this coupon!',
      icon: 'warning',
      buttons: ['No', 'Yes'],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        await dispatch(deleteCoupon(id))
      } else {
        console.log(willDelete)
      }
    })
  }

  async function handleUpdate() {
    const body = {
      discount: formData.discount,
      expiration_date: formData.expiration_date,
      code: formData.code,
    }

    await dispatch(updateCoupon(formData?.id, body))
    setFormData({ discount: '', expiration_date: '', code: '' })
  }

  return (
    <div className="bg-light d-flex flex-row align-items-center">
      <CContainer>
        <CRow className="justify-content-center">
          <CCol md={9} lg={7} xl={8}>
            <CTable align="middle" className="mb-0 border" hover responsive>
              <CTableHead color="light">
                <CTableRow>
                  <CTableHeaderCell></CTableHeaderCell>
                  <CTableHeaderCell>Code</CTableHeaderCell>
                  <CTableHeaderCell className="text-center">Discount</CTableHeaderCell>
                  <CTableHeaderCell className="text-center">Expiration date</CTableHeaderCell>
                  <CTableHeaderCell className="text-center">Status</CTableHeaderCell>
                  <CTableHeaderCell className="text-center">Action</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {coupons?.map((item, index) => (
                  <CTableRow v-for="item in tableItems" key={index}>
                    <CTableDataCell>{index + 1})</CTableDataCell>
                    <CTableDataCell>
                      <div className="clearfix">
                        <small className="text-medium-emphasis">{item.code}</small>
                      </div>
                    </CTableDataCell>
                    <CTableDataCell className="text-center">
                      <div className="clearfix">
                        <small className="text-medium-emphasis">{item.discount}</small>
                      </div>
                    </CTableDataCell>
                    <CTableDataCell className="text-center">
                      <div className="clearfix">
                        <small className="text-medium-emphasis">
                          {DateTimeConverter(item.expiration_date)}
                        </small>
                      </div>
                    </CTableDataCell>
                    <CTableDataCell className="text-center">
                      <div className="clearfix">
                        <small
                          className={` ${
                            isExpired(item.expiration_date)
                              ? 'text-danger'
                              : item.is_active === 1
                              ? 'text-success'
                              : 'text-medium-emphasis'
                          }`}
                        >
                          {isExpired(item.expiration_date)
                            ? 'Expired'
                            : item.is_active === 1
                            ? 'Active'
                            : 'Coupon Used'}
                        </small>
                      </div>
                    </CTableDataCell>
                    <CTableDataCell className="text-center">
                      <CButtonGroup role="group" aria-label="Basic mixed styles example">
                        <CButton
                          disabled={item.is_active === 0}
                          color="danger"
                          size="sm"
                          onClick={() => handleDelete(item.id)}
                        >
                          <CIcon icon={cilTrash} className="text-white" />
                        </CButton>
                        <CButton
                          disabled={item.is_active === 0}
                          color="warning"
                          size="sm"
                          onClick={() => {
                            const selectedDate = new Date(item.expiration_date)
                            const formattedDate = DateYYYYMMDD(selectedDate)
                            setFormData({ ...item, expiration_date: formattedDate })
                          }}
                        >
                          <CIcon icon={cilPencil} className="text-dark" />
                        </CButton>
                      </CButtonGroup>
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          </CCol>
          <CCol md={9} lg={7} xl={4}>
            <CCard className="mx-4">
              <CCardBody className="p-4">
                <CForm>
                  <h5 className="text-medium-emphasis mb-3">
                    {formData?.id ? 'Update' : 'Add New'} Coupon
                  </h5>
                  <CInputGroup className="mb-3">
                    <CFormInput
                      placeholder="Code (optional)"
                      autoComplete="Code"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    />
                  </CInputGroup>
                  <CInputGroup className="mb-3">
                    <CFormInput
                      placeholder="Discount"
                      autoComplete="Discount"
                      value={formData.discount}
                      onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                    />
                  </CInputGroup>
                  <CInputGroup className="mb-3">
                    <CFormInput
                      type="date"
                      placeholder="Expiration date"
                      autoComplete="Expiration date"
                      value={formData.expiration_date}
                      onChange={(e) => {
                        const selectedDate = new Date(e.target.value)
                        const formattedDate = selectedDate.toISOString().split('T')[0] // Format YYYY-MM-DD
                        setFormData({ ...formData, expiration_date: formattedDate })
                      }}
                    />
                  </CInputGroup>
                  <div className="d-grid mt-3">
                    <CButton color="success" onClick={formData?.id ? handleUpdate : handleSubmit}>
                      {formData?.id ? 'Update' : 'Submit'}
                    </CButton>
                  </div>
                </CForm>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  )
}

export default Coupons
